import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  makeStyles,
  Paper,
} from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import routes from '../../../routes';
import { navigate } from '@reach/router';
import mainColors from '../../../themes/main-colors';
import EvergyUseIcon from './icons/EvergyUseIcon';
import MyWaysToSaveIcon from './icons/MyWaysToSaveIcon';
import EnergyDemandIcon from './icons/EnergyDemandIcon';
import BillComparisonIcon from './icons/BillComparisonIcon';
import { useTranslation } from '../../../hooks/useTranslation';

const useStyles = makeStyles(theme => ({
  paper: {
    width: '100%',
  },
  matchParentHeight: {
    height: '100%',
  },
  root: {
    backgroundColor: mainColors.white,
    borderRadius: 4,
    padding: theme.spacing(2),
    width: '100%',
    maxWidth: 430,
    boxSizing: 'border-box',
    paddingBottom: '36px',
    color: mainColors.noirBlur,
  },
  fullHeightContent: {
    height: '100%',
  },
  heading: {
    fontFamily: 'Forma-DJR-Display',
    marginTop: '0',
    fontWeight: 100,
    fontSize: '20px',
    marginBottom: '0',
  },
  divider: {
    width: '100%',
    backgroundColor: mainColors.lightGray1,
    height: '1px',
    border: 0,
    margin: '8px 0',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  card: {
    backgroundColor: mainColors.riverBlue,
    padding: theme.spacing(2.5),
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 80,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#e5eff3',
    },
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flex: 1,
  },
  iconWrapper: {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: 4,
    backgroundColor: mainColors.strongBlue,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
  },
  textContainer: {
    minWidth: 0,
  },
  title: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 600,
  },
  subtitle: {
    fontSize: 15,
    lineHeight: '22px',
  },
  chevronButton: {
    padding: 0,
    marginLeft: theme.spacing(2),
  },
  chevron: {
    fontSize: 28,
  },
}));

type Props = {
  matchParentHeight?: boolean;
};

const MyEnergyInsights = ({ matchParentHeight = false }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const insightItems = useMemo(() => {
    return [
      {
        id: 'energy-use',
        title: t('MY_ENERGY_USE'),
        subtitle: t('MY_ENERGY_USE_SUBTEXT'),
        icon: <EvergyUseIcon />,
        link: routes.MY_ENERGY_USE,
      },
      {
        id: 'ways-to-save',
        title: t('MY_WAYS_TO_SAVE'),
        subtitle: t('MY_WAYS_TO_SAVE_SUBTEXT'),
        icon: <MyWaysToSaveIcon />,
        link: routes.MY_WAYS_TO_SAVE,
      },

      // "My Energy Demand" is included in the design but is hidden for the initial COM dashboard release.
      // It will be enabled once the new page is developed in the future.
      /* {
        id: 'energy-demand',
        title: t('MY_ENERGY_DEMAND'),
        subtitle: t('MY_ENERGY_DEMAND_SUBTEXT'),
        icon: <EnergyDemandIcon />,
        link: ,
      }, */
      {
        id: 'bill-comparison',
        title: t('COM_BILL_COMPARISON_TITLE'),
        subtitle: t('COM_BILL_COMPARISON_SUBTEXT'),
        icon: <BillComparisonIcon />,
        link: routes.VIEW_BILL + '?#energyTracker',
      },
    ];
  }, []);

  const handleClick = (link: string) => {
    void navigate(link);
  };

  return (
    <Paper
      className={`${classes.paper} ${
        matchParentHeight ? classes.matchParentHeight : ''
      }`}
    >
      <Box
        className={`${classes.root} ${
          matchParentHeight ? classes.fullHeightContent : ''
        }`}
      >
        <Typography component="h2" className={classes.heading}>
          {t('MY_ENERGY_INSIGHTS')}
        </Typography>

        <Box className={classes.divider} />

        <Box className={classes.cardList}>
          {insightItems.map(item => {
            return (
              <Box
                key={item.id}
                className={classes.card}
                onClick={() => handleClick(item.link)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleClick(item.link);
                  }
                }}
              >
                <Box className={classes.leftSection}>
                  <Box className={classes.iconWrapper}>{item.icon}</Box>

                  <Box className={classes.textContainer}>
                    <Typography className={classes.title}>
                      {item.title}
                    </Typography>
                    <Typography className={classes.subtitle}>
                      {item.subtitle}
                    </Typography>
                  </Box>
                </Box>

                <IconButton
                  className={classes.chevronButton}
                  aria-label={`${item.title} navigation`}
                >
                  <ChevronRightIcon className={classes.chevron} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

export default MyEnergyInsights;
